import type { Rect } from './climb-thumbnail';

/**
 * **Celebrations**: moments put on a video's timeline — the climb's card
 * bursting into confetti, its holds lighting up, or the card taking the whole
 * frame for the ending. Each starts where its marker is and lasts as long as
 * its kind does; several may overlap.
 *
 * Everything here is a function of the clip's own time, so the preview and
 * the exported video draw exactly the same frames — the confetti too, whose
 * randomness is seeded by its marker.
 */

export type CelebrationKind = 'confetti' | 'glow' | 'zoom';

export const CELEBRATION_KINDS: CelebrationKind[] = ['confetti', 'glow', 'zoom'];

/** The kinds that light or grow the climb's card: without the card on the media, they do nothing. */
export const CARD_CELEBRATIONS: CelebrationKind[] = ['glow', 'zoom'];

/** The kinds that can be put on a video, with the card on it or without. */
export function celebrationKindsFor(hasCard: boolean): CelebrationKind[] {
  return hasCard ? CELEBRATION_KINDS : CELEBRATION_KINDS.filter((kind) => !CARD_CELEBRATIONS.includes(kind));
}

export interface Celebration {
  id: string;
  kind: CelebrationKind;
  /** Where it starts, in seconds of the clip. */
  at: number;
  /** Put down by the layout: it goes when the layout is changed. */
  preset?: boolean;
  /**
   * Kept this many seconds before the end the video is cut at, as the cut
   * moves, until it is moved by hand.
   */
  fromEnd?: number;
}

/** How long each lasts, in seconds. The zoom grows in this long, and then stays. */
export const CELEBRATION_DURATION: Record<CelebrationKind, number> = {
  confetti: 2.6,
  glow: 3,
  zoom: 0.9,
};

/** After the zoom has grown, how long the video takes to darken behind the card, and how dark it gets. */
const DIM_DURATION = 1;
const DIM_MAX = 0.7;

// The holds' light: up quickly, three breaths, down.
const GLOW_RISE = 0.25;
const GLOW_FALL = 0.5;
const GLOW_BREATH = 1;
const GLOW_LOW = 0.55;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

/** How brightly the holds shine at this moment, 0–1: the brightest of the glows under way. */
export function celebrationGlow(celebrations: Celebration[], seconds: number): number {
  let brightest = 0;
  for (const celebration of celebrations) {
    if (celebration.kind !== 'glow') continue;
    const t = seconds - celebration.at;
    const duration = CELEBRATION_DURATION.glow;
    if (t < 0 || t > duration) continue;
    const envelope = Math.min(clamp01(t / GLOW_RISE), clamp01((duration - t) / GLOW_FALL));
    const breath = GLOW_LOW + (1 - GLOW_LOW) * (0.5 + 0.5 * Math.cos((2 * Math.PI * t) / GLOW_BREATH));
    brightest = Math.max(brightest, envelope * breath);
  }
  return brightest;
}

/**
 * How far into the closing scene this moment is: `grow` 0–1 as the card goes
 * from where it was put to the whole frame, `dim` 0–1 as the video darkens
 * behind it. The first zoom decides; once grown, the card stays.
 */
export function celebrationZoom(celebrations: Celebration[], seconds: number): { grow: number; dim: number } {
  const zooms = celebrations.filter((c) => c.kind === 'zoom');
  if (zooms.length === 0) return { grow: 0, dim: 0 };
  const at = Math.min(...zooms.map((c) => c.at));
  const t = seconds - at;
  if (t <= 0) return { grow: 0, dim: 0 };
  const growFor = CELEBRATION_DURATION.zoom;
  return {
    grow: easeInOut(clamp01(t / growFor)),
    dim: DIM_MAX * easeInOut(clamp01((t - growFor) / DIM_DURATION)),
  };
}

/** The confetti bursts under way at this moment: each its marker and how long it has been going. */
export function activeConfetti(celebrations: Celebration[], seconds: number): Array<{ celebration: Celebration; elapsed: number }> {
  return celebrations
    .filter((c) => c.kind === 'confetti')
    .map((celebration) => ({ celebration, elapsed: seconds - celebration.at }))
    .filter(({ elapsed }) => elapsed >= 0 && elapsed <= CELEBRATION_DURATION.confetti);
}

/**
 * Where the card goes for the ending: as big as the bounds allow (the frame,
 * or what a story's or a reel's interface leaves of it), whole and centred in
 * them. It may cover the DivePeaks mark — this is an animation, not a
 * placement — which is drawn over it.
 */
export function zoomedCardRect(
  frameWidth: number,
  frameHeight: number,
  aspect: number,
  bounds: Rect = { x: 0, y: 0, width: frameWidth, height: frameHeight },
): Rect {
  const margin = 0.05 * Math.min(frameWidth, frameHeight);
  const boxWidth = bounds.width - margin * 2;
  const boxHeight = bounds.height - margin * 2;
  const width = Math.min(boxWidth, boxHeight / aspect);
  const height = width * aspect;
  return { x: bounds.x + (bounds.width - width) / 2, y: bounds.y + (bounds.height - height) / 2, width, height };
}

/** The card's box `grow` of the way from where it was put to the ending's. */
export function interpolateRect(from: Rect, to: Rect, grow: number): Rect {
  const mix = (a: number, b: number) => a + (b - a) * grow;
  return { x: mix(from.x, to.x), y: mix(from.y, to.y), width: mix(from.width, to.width), height: mix(from.height, to.height) };
}

// ── Confetti ───────────────────────────────────────────────────────────────────

const CONFETTI_COUNT = 140;
const CONFETTI_COLOURS = ['#93C92B', '#FACC15', '#F472B6', '#38BDF8', '#FB923C', '#FFFFFF', '#A78BFA'];
/** The last stretch of a burst, in seconds, over which it fades out. */
const CONFETTI_FADE = 0.6;

/** A small seeded random generator (mulberry32): the same marker throws the same confetti every time. */
function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A number for a string, to seed a marker's confetti with. */
export function seedOf(id: string): number {
  let hash = 2166136261;
  for (let i = 0; i < id.length; i++) hash = Math.imul(hash ^ id.charCodeAt(i), 16777619);
  return hash >>> 0;
}

export interface ConfettiPiece {
  x: number;
  y: number;
  width: number;
  height: number;
  angle: number;
  colour: string;
  alpha: number;
}

/**
 * Where every piece of a burst is, `elapsed` seconds in: thrown up and out from
 * `origin`, falling back under gravity, tumbling, and fading at the end. Sized
 * against the frame's short edge, so it looks the same on any video.
 */
export function confettiPieces(seed: number, elapsed: number, origin: { x: number; y: number }, frameWidth: number, frameHeight: number): ConfettiPiece[] {
  const random = seededRandom(seed);
  const short = Math.min(frameWidth, frameHeight);
  const gravity = 1.6 * frameHeight;
  const alpha = clamp01((CELEBRATION_DURATION.confetti - elapsed) / CONFETTI_FADE);
  const pieces: ConfettiPiece[] = [];
  for (let i = 0; i < CONFETTI_COUNT; i++) {
    // Every draw is taken whatever the moment, so piece i is the same piece throughout.
    const direction = -Math.PI / 2 + (random() - 0.5) * (Math.PI * 0.75);
    const speed = (0.55 + random() * 0.75) * frameHeight;
    const size = (0.012 + random() * 0.014) * short;
    const spin = (random() - 0.5) * 16;
    const sway = (random() - 0.5) * 0.08 * short;
    const swayRate = 3 + random() * 4;
    const colour = CONFETTI_COLOURS[Math.floor(random() * CONFETTI_COLOURS.length)];
    // Air slows the pieces down, so they flutter rather than fly.
    const drag = 1 - Math.exp(-1.8 * elapsed);
    const travel = drag / 1.8;
    pieces.push({
      x: origin.x + Math.cos(direction) * speed * travel + sway * Math.sin(swayRate * elapsed),
      y: origin.y + Math.sin(direction) * speed * travel + 0.5 * gravity * elapsed * elapsed * 0.35,
      width: size,
      height: size * 0.55 * Math.abs(Math.cos(spin * elapsed)),
      angle: spin * elapsed * 0.6,
      colour,
      alpha,
    });
  }
  return pieces;
}

export function drawConfetti(ctx: CanvasRenderingContext2D, pieces: ConfettiPiece[]) {
  ctx.save();
  for (const piece of pieces) {
    if (piece.alpha <= 0) continue;
    ctx.save();
    ctx.globalAlpha = piece.alpha;
    ctx.translate(piece.x, piece.y);
    ctx.rotate(piece.angle);
    ctx.fillStyle = piece.colour;
    ctx.fillRect(-piece.width / 2, -piece.height / 2, piece.width, Math.max(1, piece.height));
    ctx.restore();
  }
  ctx.restore();
}
